// Centralised legal / public-info constants for My Fleet.
// Any change to the support email, publisher information, or last-update date
// MUST happen here — never inline in components.
//
// This module intentionally exports plain strings and shallow objects: it is
// consumed by both React components (Privacy/Terms/LegalMentions/DeleteAccountPublic)
// and by tests. Do not add runtime side-effects here.

export const APP_NAME = "My Fleet";

// Sole public contact for support, legal, and RGPD.
export const SUPPORT_EMAIL = "support.myfleet@gmail.com";

// Public documents versioning. Bump these when the text of Privacy/Terms/Legal
// materially changes. Displayed on every legal page.
export const LEGAL_LAST_UPDATED = "2026-07-20";
export const LEGAL_DOC_VERSION = "1.1";

// Production public URL. In the Preview environment REACT_APP_PUBLIC_URL is
// still injected so canonical/OG tags stay correct locally. There is NO Preview
// fallback here — the production domain is the single source of truth used by
// every legal page (canonical/OG). If neither env var is set (unlikely), the
// domain still resolves to https://myfleetapp.fr.
const _ENV_PUB = (typeof process !== "undefined" && process.env && process.env.REACT_APP_PUBLIC_URL) || "";
const _ENV_BACK = (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) || "";
const _ENV_MODE = ((typeof process !== "undefined" && process.env && process.env.REACT_APP_ENV) || "").toLowerCase();
export const APP_PUBLIC_URL = "https://myfleetapp.fr";
// True when we are NOT running against the production deployment (URL bar of
// the dev/preview environment). Consumed only to display a small "URL
// provisoire" hint under the H1 in dev. Legal content itself never uses this.
export const IS_PREVIEW = _ENV_MODE !== "production"
  && (_ENV_PUB.indexOf("myfleetapp.fr") === -1)
  && (_ENV_BACK.indexOf("myfleetapp.fr") === -1);

export const LEGAL_ROUTES = {
  privacy: "/privacy",
  terms: "/terms",
  legal: "/legal",
  deleteAccount: "/delete-account",
  support: "/support",
  login: "/login",
};

// Confirmed, publishable legal information. No placeholder text is ever
// exposed. Ambiguous items (host legal entity, MongoDB region) use prudent
// wording that only claims what has been actually confirmed.
export const LEGAL_INFO = {
  // Editor — deliberate anonymity. Emergent's ability to hand over the
  // editor's identity has not been confirmed, so we do not rely on the
  // LCEN "anonymous publisher" formulation.
  publisherStatement:
    "My Fleet est édité à titre non professionnel par une personne physique résidant en France.",
  publisherContact: SUPPORT_EMAIL,

  // Host / infrastructure — factual, prudent, no invented legal entity.
  hostName: "Emergent",
  hostStatement:
    "Hébergement et infrastructure technique : service Emergent. Les traitements techniques peuvent être réalisés aux États-Unis et en Inde. Les informations juridiques complètes du fournisseur d'hébergement sont en cours de confirmation auprès de celui-ci.",
  hostPrivacyContact: "privacy@emergent.sh",
  hostTermsUrl: "https://app.emergent.sh/terms-of-service",
  hostPrivacyUrl: "https://app.emergent.sh/privacy-policy",

  // Applicable law — validated verbatim by the editor.
  applicableLaw:
    "Les présentes conditions sont régies par le droit français, sous réserve des dispositions impératives protectrices applicables aux consommateurs. En cas de difficulté, l'utilisateur est invité à contacter préalablement " + SUPPORT_EMAIL + " afin de rechercher une solution amiable. À défaut, le litige relève des juridictions compétentes conformément aux règles légales applicables.",
};

// Kept for backward compatibility with any test or consumer still importing
// this symbol. All values are now confirmed and no placeholder remains.
export const LEGAL_PLACEHOLDERS = Object.freeze({});

// True if any placeholder is still present. Always false now — kept so that
// consumers (LegalPreviewBanner) can be removed later without breaking.
export function hasPendingLegalPlaceholders() {
  return false;
}

// Common metadata helpers. Each legal page passes its own title + description.
export function buildCanonicalUrl(pathname) {
  const base = APP_PUBLIC_URL.replace(/\/+$/, "");
  const clean = pathname.startsWith("/") ? pathname : `/${pathname}`;
  return `${base}${clean}`;
}
